import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import WarningIcon from '@mui/icons-material/Warning';
import { RootState } from '../store';
import { LandmineAlert } from './LandmineAlert';
import { ALERT_BADGE_LABEL, AlertViewer, LANDMINE_PALETTE, speakerLabel } from '../constants/alertTheme';
import { useAlertViewer } from '../contexts/SessionViewerContext';
import {
  STRIP_DROPDOWN_CLASS,
  STRIP_FOOTER_CLASS,
  STRIP_PILL_CLASS,
  STRIP_PREVIEW_CLASS,
  STRIP_TITLE_GROUP_CLASS,
  StripChevron,
  StripEmptyState,
  StripRule,
} from './summaryStrip';
import { LANDMINE_BAR_ALERTING_OPACITY, LANDMINE_BAR_MOTION } from '../constants/landmineBar';
import {
  INITIAL_LANDMINE_BAR_ATTENTION,
  LandmineListExpansion,
  expansionOnCountChange,
  expansionOnOpen,
  isLandmineBarAlerting,
  landmineCountChanged,
  landmineSpeakerPrefix,
  markLandminesSeen,
  toggleExpansion,
} from '../utils/landmineBarLogic';

type StoredLandmine = RootState['landmine']['alerts'][number];

const landmineKey = (alert: StoredLandmine): string => `${alert?.trigger}-${alert?.detected_at}`;

/**
 * Collapsible top-bar column holding every landmine of the call, beside
 * LiveTranscriptBar and NewInquiryBar. Mirrors `LandmineBarView.swift`.
 *
 * Landmines used to arrive as corner cards; with the detector's false-positive
 * rate that was a stream of interruptions mid-call. Here the only interruption
 * is a one-line strip that glides out from under the header, holds briefly and
 * glides back. The header then stays red until the rep opens the list, so a
 * landmine can be ignored in the moment but never missed. When the cue fires is
 * decided in `utils/landmineBarLogic.ts`, shared in spirit with both desktops.
 */
export const LandmineBar: React.FC = () => {
  // Arrival order, oldest first (the slice appends); rendered as-is, never sorted.
  const alerts = useSelector((state: RootState) => state.landmine?.alerts ?? []);
  const latest = alerts.length > 0 ? alerts[alerts.length - 1] : null;
  // Labels by raiser: "You" only for the viewer's own landmine.
  const viewer = useAlertViewer();

  const [isExpanded, setIsExpanded] = useState(false);
  const [attention, setAttention] = useState(INITIAL_LANDMINE_BAR_ATTENTION);
  const [pulseKey, setPulseKey] = useState(0);
  const [flyInAlert, setFlyInAlert] = useState<StoredLandmine | null>(null);
  const [isFlyInVisible, setIsFlyInVisible] = useState(false);
  const [rowExpansion, setRowExpansion] = useState<LandmineListExpansion>(() => new Set());

  const previousCountRef = useRef(alerts.length);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showFrameRef = useRef<number | null>(null);
  const feedRef = useRef<HTMLDivElement>(null);

  const isHeaderRed = isLandmineBarAlerting(attention) && !isExpanded;

  const hideFlyIn = useCallback(() => {
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
    setIsFlyInVisible(false);
  }, []);

  /**
   * A burst updates the visible strip in place (its content is keyed, so the
   * text crossfades) and restarts the hold, rather than stacking strips or
   * replaying the entrance. The visibility flip waits a frame so a freshly
   * mounted strip starts from its hidden position and actually transitions.
   */
  const showFlyIn = useCallback(
    (alert: StoredLandmine) => {
      setFlyInAlert(alert);
      if (showFrameRef.current !== null) cancelAnimationFrame(showFrameRef.current);
      showFrameRef.current = requestAnimationFrame(() => {
        showFrameRef.current = null;
        setIsFlyInVisible(true);
      });
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(hideFlyIn, LANDMINE_BAR_MOTION.holdMs);
    },
    [hideFlyIn]
  );

  useEffect(() => {
    const oldCount = previousCountRef.current;
    const newCount = alerts.length;
    if (oldCount === newCount) return;
    previousCountRef.current = newCount;

    const arrivedLive = !latest?.isRestored;
    const change = landmineCountChanged(attention, oldCount, newCount, isExpanded, arrivedLive);
    setAttention(change.attention);
    if (isExpanded) setRowExpansion((expansion) => expansionOnCountChange(expansion, oldCount, newCount));
    if (newCount === 0) hideFlyIn();
    if (change.shouldFlyIn && latest) {
      setPulseKey((key) => key + 1);
      showFlyIn(latest);
    }
  }, [alerts.length, attention, isExpanded, latest, showFlyIn, hideFlyIn]);

  useEffect(
    () => () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (showFrameRef.current !== null) cancelAnimationFrame(showFrameRef.current);
    },
    []
  );

  useEffect(() => {
    if (!isExpanded) return;
    requestAnimationFrame(() => {
      if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
    });
  }, [alerts.length, isExpanded]);

  const handleToggle = () => {
    const opening = !isExpanded;
    setIsExpanded(opening);
    if (opening) {
      setRowExpansion(expansionOnOpen(alerts.length));
      setAttention(markLandminesSeen());
      hideFlyIn();
    }
  };

  const handleRowToggle = (index: number) => {
    setRowExpansion((expansion) => toggleExpansion(expansion, index));
  };

  const headerInk = isHeaderRed ? { color: LANDMINE_PALETTE.heading } : undefined;

  return (
    <>
      {/* Collapsed bar. The alerting tint sits under the row's content, which is
          `relative` so it paints above the tint. */}
      <div
        className={`relative cursor-pointer px-4 py-2 ${isHeaderRed ? '' : 'hover:bg-surface-subtle'}`}
        onClick={handleToggle}
        title={isHeaderRed ? 'New landmines flagged. Click to review.' : 'Landmines flagged during this call'}
      >
        <span
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background: LANDMINE_PALETTE.badge,
            opacity: isHeaderRed ? LANDMINE_BAR_ALERTING_OPACITY : 0,
            transition: `opacity ${LANDMINE_BAR_MOTION.headerFadeMs}ms ease-in-out`,
          }}
        />
        <div className="relative flex items-center gap-2.5">
          <div className={STRIP_TITLE_GROUP_CLASS}>
            <WarningIcon
              sx={{ fontSize: 10, color: isHeaderRed ? LANDMINE_PALETTE.heading : LANDMINE_PALETTE.accent }}
            />
            <h3 className="text-xs font-semibold text-ink" style={headerInk}>
              Landmines
            </h3>
            {alerts.length > 0 && (
              <span
                key={pulseKey}
                className={`${STRIP_PILL_CLASS} ${pulseKey > 0 ? 'animate-badge-pulse' : ''}`}
                style={{
                  background: LANDMINE_PALETTE.badge,
                  color: LANDMINE_PALETTE.badgeText,
                }}
              >
                {alerts.length}
              </span>
            )}
          </div>

          <StripRule />

          {latest ? (
            <div className={STRIP_PREVIEW_CLASS}>
              <span className="flex-shrink-0 font-medium text-ink-secondary" style={headerInk}>
                {landmineSpeakerPrefix(latest, viewer) ?? 'Latest'}:
              </span>
              <span
                key={landmineKey(latest)}
                className="truncate text-ink-muted animate-fade-in"
                style={headerInk}
              >
                {latest?.trigger}
              </span>
            </div>
          ) : (
            <span className="min-w-0 flex-1 truncate text-[11px] text-ink-muted">No landmines yet</span>
          )}

          <button
            className={`flex-shrink-0 p-0.5 transition-colors ${
              isHeaderRed ? '' : 'text-ink-muted hover:text-ink-secondary'
            }`}
            style={headerInk}
            aria-label={isExpanded ? 'Collapse landmines' : 'Expand landmines'}
            onClick={(e) => {
              e.stopPropagation();
              handleToggle();
            }}
          >
            <StripChevron isExpanded={isExpanded} />
          </button>
        </div>
      </div>

      {!isExpanded && flyInAlert && <LandmineFlyInStrip alert={flyInAlert} isVisible={isFlyInVisible} viewer={viewer} />}

      {/* Expanded dropdown — contained within this bar's own relative column */}
      {isExpanded && (
        <div className={STRIP_DROPDOWN_CLASS}>
          <div ref={feedRef} className="max-h-accordion overflow-y-auto bg-paper p-2.5" style={{ scrollBehavior: 'smooth' }}>
            {alerts.length === 0 ? (
              <StripEmptyState title="No landmines yet" detail="Landmines flagged during the call will appear here" />
            ) : (
              <div>
                {alerts.map((alert, index) => (
                  <LandmineAlert
                    key={index}
                    alert={alert}
                    isCollapsed={!rowExpansion.has(index)}
                    onToggleCollapse={() => handleRowToggle(index)}
                  />
                ))}
              </div>
            )}
          </div>

          {alerts.length > 0 && (
            <div className={STRIP_FOOTER_CLASS}>
              {alerts.length} landmine{alerts.length === 1 ? '' : 's'} flagged
            </div>
          )}
        </div>
      )}
    </>
  );
};

/**
 * One line: what kind, which landmine, whose words — a sliver of the landmine
 * card so the rep already knows how to read it. It sits in a clipped box right
 * under the header, so sliding from -100% reads as the strip coming out from
 * beneath the bar and going back under it. It is a glance, not a control:
 * pointer-events are off so it never eats a click meant for the page.
 */
const LandmineFlyInStrip: React.FC<{ alert: StoredLandmine; isVisible: boolean; viewer: AlertViewer }> = ({
  alert,
  isVisible,
  viewer,
}) => (
  <div
    data-testid="landmine-fly-in"
    className="absolute left-0 right-0 top-full overflow-hidden pointer-events-none z-40 pb-2"
    aria-hidden={!isVisible}
    aria-live="polite"
  >
    <div
      className={`transition-[transform,opacity] motion-reduce:translate-y-0 ${
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      }`}
      style={{
        transitionDuration: `${isVisible ? LANDMINE_BAR_MOTION.enterMs : LANDMINE_BAR_MOTION.exitMs}ms`,
        transitionTimingFunction: isVisible ? LANDMINE_BAR_MOTION.enterEasing : LANDMINE_BAR_MOTION.exitEasing,
      }}
    >
      <div
        className="flex items-center gap-2 px-3 py-2 shadow-md"
        style={{
          background: LANDMINE_PALETTE.surface,
          borderLeft: `3px solid ${LANDMINE_PALETTE.accent}`,
          borderBottom: `1px solid ${LANDMINE_PALETTE.border}`,
        }}
      >
        <div key={landmineKey(alert)} className="flex items-center gap-2 min-w-0 animate-fade-in">
          <span
            className="flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold tracking-[0.08em]"
            style={{ background: LANDMINE_PALETTE.badge, color: LANDMINE_PALETTE.badgeText }}
          >
            {ALERT_BADGE_LABEL.landmine}
          </span>
          <span className="truncate text-xs font-semibold" style={{ color: LANDMINE_PALETTE.heading }}>
            {alert?.trigger}
          </span>
          <span className="flex-shrink-0 text-[11px] font-medium" style={{ color: LANDMINE_PALETTE.muted }}>
            {speakerLabel(alert, viewer)}
          </span>
        </div>
      </div>
    </div>
  </div>
);
